import React, { useMemo, useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { Card, Col, Row } from 'react-bootstrap';
import { RHFSelect } from 'components/common/form';
import PropTypes from 'prop-types';
import { locationTypesMap } from 'components/donations/donation-settings/Locations';
import { getCitiesOptions } from 'data/options/countris-and-cities';
import { useLocations } from 'hooks/useBeneficiaries';
import useBranchCountry from 'hooks/useBranchCountry';
import { getDefaultLanguage } from 'data/options/countryLanguages';

export const getCountriesOptions = locations =>
  locations.map(location => ({
    label: location.location_name,
    value: location.location_name
  }));

export const LocationCard = ({ sectionRef, sideNavLink, country }) => {
  const { locations } = useLocations(locationTypesMap.sponsorship);
  const { branchCountry, restrictToBranchCountry } = useBranchCountry();
  const { setValue, getValues } = useFormContext();

  // Main Language follows the country: filled when empty, and swapped when
  // it still holds the previous country's default. Anything typed by hand
  // (or loaded from an existing record) is left alone.
  const prevCountry = useRef(country);
  useEffect(() => {
    const previous = prevCountry.current;
    prevCountry.current = country;
    if (!country || country === previous) return;
    const next = getDefaultLanguage(country);
    if (!next) return;
    const current = (getValues('language') || '').trim();
    const prevDefault = getDefaultLanguage(previous);
    if (
      !current ||
      (prevDefault && current.toLowerCase() === prevDefault.toLowerCase())
    ) {
      setValue('language', next, { shouldValidate: true, shouldDirty: true });
    }
  }, [country, getValues, setValue]);

  // Ground teams may only add beneficiaries in their own branch's country
  // (the branch's Location setting); HQ sees every country.
  const countriesOptions = useMemo(() => {
    const all = locations ? getCountriesOptions(locations) : [];
    if (restrictToBranchCountry && branchCountry) {
      const own = all.filter(
        o => o.value.toLowerCase() === branchCountry.toLowerCase()
      );
      return own.length
        ? own
        : [{ label: branchCountry, value: branchCountry }];
    }
    return all;
  }, [locations, restrictToBranchCountry, branchCountry]);

  useEffect(() => {
    if (restrictToBranchCountry && branchCountry && country !== branchCountry) {
      setValue('location.country', branchCountry, { shouldValidate: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restrictToBranchCountry, branchCountry]);

  const citiesOptions = useMemo(() => {
    if (country) {
      return getCitiesOptions(country);
    }
    return [];
  }, [country]);

  return (
    <Card className="border shadow-none mb-3" ref={sectionRef} id={sideNavLink}>
      <Card.Header className="pb-0">
        <div className="dFlexRowStartSpace pt-3">
          <Card.Title as="h5">Location</Card.Title>
        </div>
      </Card.Header>
      <Card.Body>
        <Row className="gx-3 gy-3">
          <Col xs={12} sm={6} className="pt-3">
            <RHFSelect
              name="location.country"
              label="Country"
              required
              options={countriesOptions}
              isDisabled={restrictToBranchCountry && !!branchCountry}
              infoMessage={
                restrictToBranchCountry && branchCountry
                  ? `Beneficiaries for this branch are registered in ${branchCountry}.`
                  : undefined
              }
            />
          </Col>
          <Col xs={12} sm={6} className="pt-3">
            <RHFSelect
              name="location.city"
              label="City"
              required
              options={citiesOptions}
              isDisabled={citiesOptions.length === 0}
            />
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

LocationCard.propTypes = {
  sectionRef: PropTypes.object,
  sideNavLink: PropTypes.string,
  country: PropTypes.string
};
